// Suggested Kiri quick-actions — change by role, current page and whether a
// job is open. Pure UI convenience: clicking one just sends a normal chat
// message, which the backend re-validates against the real permission/action
// registry (this file has no authority of its own).
const OFFICE_ROLES = ["super_admin", "admin", "office"];
const OFFICE_QS_ROLES = ["super_admin", "admin", "office", "quantity_surveyor"];

function pageKeyFromPath(pathname) {
  if (/^\/app\/financials/.test(pathname)) return "financials";
  if (/^\/app\/jobs\/[^/]+/.test(pathname)) return "job";
  if (/^\/app\/jobs/.test(pathname)) return "jobs";
  if (/^\/app\/timesheets/.test(pathname)) return "timesheets";
  if (/^\/app\/(fleet|gear)/.test(pathname)) return "plant";
  if (/^\/app\/safety/.test(pathname)) return "safety";
  if (/^\/app\/qa/.test(pathname)) return "qa";
  if (/^\/app\/quotes/.test(pathname)) return "quotes";
  return null;
}

const PAGE_SUGGESTIONS = {
  financials: [
    { label: "Explain job margin", message: "How do I read job margin?" },
    { label: "Show low-margin jobs", message: "Which jobs have the lowest margin?" },
    { label: "Show unpaid invoices", message: "Show unpaid invoices" },
  ],
  job: [
    { label: "Summarise this job", message: "Summarise this job" },
    { label: "Write today's site diary", message: "Write today's site diary for this job" },
    { label: "Add a task", message: "Add a task to this job" },
  ],
  jobs: [
    { label: "Create a job", message: "Create a new job" },
    { label: "Find a job", message: "Find a job" },
  ],
  timesheets: [
    { label: "How do I approve a timesheet?", message: "How do I approve a timesheet?" },
    { label: "What's missing?", message: "What timesheets are outstanding?" },
  ],
  plant: [
    { label: "Find plant", message: "Find a vehicle or machine" },
    { label: "What's due for service?", message: "What's due for service?" },
  ],
  safety: [
    { label: "Take me to pre-starts", message: "Take me to pre-starts" },
    { label: "Take me to toolbox talks", message: "Take me to toolbox talks" },
  ],
  qa: [
    { label: "Show incomplete inspections", message: "What site inspections are incomplete?" },
    { label: "Find a QA record", message: "Find a QA record" },
  ],
  quotes: [
    { label: "Find a quote", message: "Find a quote" },
    { label: "Summarise outstanding quotes", message: "Summarise my outstanding quotes" },
  ],
};

const ROLE_DEFAULTS = {
  field: [
    { label: "Find a job", message: "Find a job" },
    { label: "Open timesheets", message: "Take me to timesheets" },
    { label: "Open safety forms", message: "Take me to safety" },
    { label: "How do I log hours?", message: "How do I log my hours?" },
  ],
  mechanic: [
    { label: "Open fleet", message: "Take me to fleet" },
    { label: "Open gear", message: "Take me to gear" },
    { label: "Find a job", message: "Find a job" },
  ],
  quantity_surveyor: [
    { label: "Open rate library", message: "Take me to the rate library" },
    { label: "Open quotes", message: "Take me to quotes" },
    { label: "Find a client", message: "Find a client" },
  ],
  office: [
    { label: "Create a job", message: "Create a new job" },
    { label: "Find a job", message: "Find a job" },
    { label: "Open crew planner", message: "Take me to crew planner" },
    { label: "How do I approve a timesheet?", message: "How do I approve a timesheet?" },
  ],
  admin: [
    { label: "Create a job", message: "Create a new job" },
    { label: "Open financials", message: "Take me to financials" },
    { label: "Find a client", message: "Find a client" },
    { label: "Open team directory", message: "Take me to team" },
  ],
  super_admin: [
    { label: "Open platform admin", message: "Take me to the platform admin area" },
    { label: "Find a job", message: "Find a job" },
  ],
};

// Home-state quick actions + context-aware suggestions (max 6 total).
export function getSuggestedActions(role, pathname) {
  const pageKey = pageKeyFromPath(pathname || "");
  const contextual = PAGE_SUGGESTIONS[pageKey] || [];
  const defaults = ROLE_DEFAULTS[role] || ROLE_DEFAULTS.field;
  const seen = new Set();
  const combined = [...contextual, ...defaults].filter((s) => {
    if (seen.has(s.label)) return false;
    seen.add(s.label);
    return true;
  });
  return combined.slice(0, 6);
}

export function currentPageLabel(pathname) {
  const key = pageKeyFromPath(pathname || "");
  const labels = {
    financials: "Financials", job: "Job", jobs: "Jobs", timesheets: "Timesheets",
    plant: "Plant & Fleet", safety: "Safety", qa: "QA", quotes: "Quotes",
  };
  return labels[key] || null;
}

// "What are we making?" menu — only lists things Kiri can genuinely help
// with today: real draft actions, or a direct link to an existing page.
export function getCreateMenu(role) {
  const items = [
    { label: "Job", message: "Create a new job", roles: OFFICE_ROLES },
    { label: "Task", message: "Add a task to a job", roles: null },
    { label: "Site Diary", message: "Write today's site diary for this job", roles: null },
    { label: "Quote", message: "Take me to create a quote", roles: OFFICE_QS_ROLES },
    { label: "Variation", message: "Take me to variations", roles: OFFICE_QS_ROLES },
    { label: "Pre-Start", message: "Take me to pre-starts", roles: null },
    { label: "Toolbox Talk", message: "Take me to toolbox talks", roles: null },
    { label: "QA Record", message: "Take me to QA records", roles: null },
  ];
  return items.filter((i) => !i.roles || i.roles.includes(role));
}
